import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  BarChart3, 
  CheckCircle2, 
  AlertTriangle, 
  TrendingUp, 
  FileText,
  Layers,
  Clock,
  ChevronRight,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';

const AdminPromptStats = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/prompt-stats');
      setStats(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <div className="flex items-center gap-3 text-text-muted">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Cargando estadísticas...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <p className="text-red-400">{error}</p>
          <button 
            onClick={fetchStats}
            className="mt-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-300 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const { summary, by_strategy, by_variant, by_asset_type, missing, recent_updates } = stats;

  // Calculate health status
  const getHealthStatus = () => {
    if (summary.coverage_percent >= 95) return { label: 'Excelente', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (summary.coverage_percent >= 80) return { label: 'Bueno', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    return { label: 'Necesita Atención', color: 'text-red-400', bg: 'bg-red-500/20' };
  };
  const health = getHealthStatus();

  // Strategy colors
  const strategyColors = {
    'lanzamiento': 'bg-blue-500',
    'evergreen': 'bg-green-500',
    'plf': 'bg-purple-500',
    'ecommerce': 'bg-pink-500',
    'high-ticket': 'bg-amber-500',
    'desafio': 'bg-cyan-500',
    'membresia': 'bg-indigo-500',
    'saas': 'bg-teal-500',
    'organico': 'bg-lime-500',
    'local': 'bg-orange-500',
    'agentes-ia': 'bg-rose-500'
  };

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: BarChart3 },
    { id: 'strategies', label: 'Por Estrategia', icon: Layers },
    { id: 'assets', label: 'Por Tipo', icon: FileText },
    { id: 'missing', label: 'Faltantes', icon: AlertTriangle },
    { id: 'recent', label: 'Recientes', icon: Clock }
  ];

  return (
    <div className="min-h-screen bg-app-bg p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <button 
              onClick={() => navigate('/admin/prompts')}
              className="p-2 hover:bg-surface-card rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-text-muted" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-text-main">Estadísticas de Prompts</h1>
              <p className="text-text-muted text-xs sm:text-sm">Panel de monitoreo del sistema</p>
            </div>
          </div>
          <button 
            onClick={fetchStats}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-accent-primary/20 hover:bg-accent-primary/30 text-accent-primary rounded-lg transition-colors w-full sm:w-auto"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-surface-card border border-border-subtle rounded-xl p-4 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="p-1.5 sm:p-2 bg-accent-primary/20 rounded-lg">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-accent-primary" />
              </div>
              <span className="text-text-muted text-xs sm:text-sm">Total Prompts</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-text-main">{summary.total_prompts.toLocaleString()}</p>
          </div>

          <div className="bg-surface-card border border-border-subtle rounded-xl p-4 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="p-1.5 sm:p-2 bg-purple-500/20 rounded-lg">
                <Layers className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
              </div>
              <span className="text-text-muted text-xs sm:text-sm">Estrategias</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-text-main">{summary.total_strategies}</p>
          </div>

          <div className="bg-surface-card border border-border-subtle rounded-xl p-4 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="p-1.5 sm:p-2 bg-cyan-500/20 rounded-lg">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
              </div>
              <span className="text-text-muted text-xs sm:text-sm">Variantes</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-text-main">
              {summary.total_variants}
              <span className="text-base sm:text-lg text-text-muted font-normal">/{summary.expected_variants}</span>
            </p>
          </div>

          <div className="bg-surface-card border border-border-subtle rounded-xl p-4 sm:p-5">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className={`p-1.5 sm:p-2 ${health.bg} rounded-lg`}>
                <CheckCircle2 className={`w-4 h-4 sm:w-5 sm:h-5 ${health.color}`} />
              </div>
              <span className="text-text-muted text-xs sm:text-sm">Cobertura</span>
            </div>
            <p className={`text-2xl sm:text-3xl font-bold ${health.color}`}>{summary.coverage_percent}%</p>
            <p className={`text-xs ${health.color} mt-1`}>{health.label}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 sm:mb-6 border-b border-border-subtle pb-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg whitespace-nowrap transition-colors text-sm ${
                activeTab === tab.id 
                  ? 'bg-accent-primary text-white' 
                  : 'text-text-muted hover:bg-surface-card'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.id === 'missing' && summary.missing_count > 0 && (
                <span className="ml-1 px-1.5 sm:px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                  {summary.missing_count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-surface-card border border-border-subtle rounded-xl p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-text-main mb-4">Distribución por Estrategia</h3>
              <div className="space-y-3">
                {Object.entries(by_strategy).sort((a, b) => b[1] - a[1]).map(([strategy, count]) => {
                  const percentage = Math.round((count / summary.total_prompts) * 100);
                  return (
                    <div key={strategy} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-text-main capitalize font-medium">{strategy}</span>
                        <span className="text-text-muted text-sm">{count} prompts ({percentage}%)</span>
                      </div>
                      <div className="h-3 bg-surface-elevated rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${strategyColors[strategy] || 'bg-gray-500'} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Strategies Tab */}
          {activeTab === 'strategies' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-text-main mb-4">Desglose por Variante</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(by_strategy).map(([strategy, totalCount]) => {
                  const variants = by_variant.filter(v => v.strategy === strategy);
                  return (
                    <div key={strategy} className="bg-surface-elevated rounded-lg p-4 border border-border-subtle">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-text-main capitalize flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${strategyColors[strategy] || 'bg-gray-500'}`} />
                          {strategy}
                        </h4>
                        <span className="text-sm text-text-muted">{totalCount} total</span>
                      </div>
                      <div className="space-y-2">
                        {variants.map(v => (
                          <div key={v.variant} className="flex items-center justify-between text-sm">
                            <span className="text-text-muted">{v.variant}</span>
                            <span className="text-text-main font-medium">{v.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Assets Tab */}
          {activeTab === 'assets' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-text-main mb-4">Distribución por Tipo de Activo</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(by_asset_type).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                  <div key={type} className="bg-surface-elevated rounded-lg p-4 border border-border-subtle text-center">
                    <div className="text-2xl font-bold text-text-main mb-1">{count}</div>
                    <div className="text-sm text-text-muted capitalize">{type || 'otros'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Missing Tab */}
          {activeTab === 'missing' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-text-main mb-4">
                Variantes Faltantes
                {missing.length === 0 && <span className="text-green-400 text-sm ml-2">¡Todo completo!</span>}
              </h3>
              {missing.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <p className="text-text-main font-medium">Todas las estrategias y variantes están pobladas</p>
                  <p className="text-text-muted text-sm mt-2">El sistema de prompts está completo al 100%</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {missing.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                        <div>
                          <span className="text-text-main font-medium capitalize">{item.strategy}</span>
                          {item.variant && (
                            <span className="text-text-muted ml-2">→ {item.variant}</span>
                          )}
                        </div>
                      </div>
                      <span className="text-red-400 text-sm">{item.issue}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Recent Tab */}
          {activeTab === 'recent' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-text-main mb-4">Últimas Actualizaciones</h3>
              <div className="space-y-3">
                {recent_updates.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-surface-elevated rounded-lg p-4 border border-border-subtle">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-text-muted" />
                      <div>
                        <p className="text-text-main font-medium">{item.name || item.asset_type_id}</p>
                        <p className="text-text-muted text-sm">
                          {item.strategy_id} → {item.variant_id || 'global'}
                        </p>
                      </div>
                    </div>
                    <span className="text-text-muted text-xs">
                      {item.updated_at ? new Date(item.updated_at).toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'N/A'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-6 flex justify-center gap-4">
          <button
            onClick={() => navigate('/admin/prompts')}
            className="flex items-center gap-2 px-6 py-3 bg-accent-primary hover:bg-accent-primary/80 text-white rounded-lg transition-colors"
          >
            Ir a Editor de Prompts
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPromptStats;
